import { EnumButtonName, EventProperties } from '@calm/evexia-analytics';
import { DS_HEIGHT, DS_SHAPE_BORDER_RADIUS } from '@calm/evexia-design-tokens';
import {
    Box,
    Button,
    ButtonBase,
    Grid,
    Paper,
    ButtonProps,
    Typography,
    SxProps,
    Theme,
    useTheme,
    useMediaQuery,
    TypographyProps,
    Skeleton,
} from '@rhg/libs/material';
import { sxAppend } from '@rhg/libs/material/sx';
import { getRandomIntBetween } from '@rhg/utils/math';
import React, {
    PropsWithChildren,
    ReactElement,
    ReactNode,
    useMemo,
} from 'react';
import StopPropagation from '../stop-propagation';

const stylePaper = { position: 'relative' };
const styleOverlaidButton = {
    zIndex: 2,
    position: 'absolute',
    top: 0,
    left: 0,
    height: '100%',
    width: '100%',
};

const styleContent = (padding: number) => ({ p: padding });

const styleChildrenWrapper = {
    display: 'block',
    textAlign: 'left',
    width: '100%',
};

const styleImage = {
    width: DS_HEIGHT.BANNER_SIZE_SMALL,
    height: DS_HEIGHT.BANNER_SIZE_SMALL,
    borderRadius: `${DS_SHAPE_BORDER_RADIUS.CARD}px`,
    overflow: 'hidden',
    position: 'relative',
    '& img': {
        objectFit: 'cover',
        width: '100%',
        aspectRatio: '1',
        verticalAlign: 'bottom',
    },
};

const styleImageMedium = {
    width: DS_HEIGHT.BANNER_SIZE_MEDIUM,
    height: DS_HEIGHT.BANNER_SIZE_MEDIUM,
};

const styleImageLarge = {
    width: DS_HEIGHT.BANNER_SIZE_LARGE,
    height: DS_HEIGHT.BANNER_SIZE_LARGE,
};

const styleSubtitle = (spacing: number) => ({
    mt: spacing,
});

const styleButton = { textWrap: 'nowrap' };

const styleCaptionWrapper = { mt: 4 };

const styleImageLoadingWrapper = {
    height: '100%',
    width: '100%',
    display: 'flex',
    alignItems: 'center',
};

const styleImageLoading = (contentPadding: number) => ({
    height: `calc(100% - ${contentPadding > 0 ? 0 : 32}px)`,
    width: `calc(100% - ${contentPadding > 0 ? 0 : 32}px)`,
    borderRadius: 2,
    ml: contentPadding > 0 ? 0 : 4,
});

type ActionProps = Partial<
    Omit<ButtonProps, 'trackButton'> & {
        component?: React.ElementType;
        href?: string;
    }
>;

export interface CustomBannerAction {
    isCustom: true;
    customAction: ReactNode;
}

export interface BannerAction {
    actionText: string;
    onClickAction?: () => void;
    trackAction: EnumButtonName;
    trackProps?: EventProperties;
    actionProps?: ActionProps;
    isCustom?: false;
}

type ImageSize = 'small' | 'medium' | 'large';

interface BannerBaseProps {
    title: string;
    subtitle?: string;
    category?: string;
    leftImage: ReactNode;
    leftImageSize?: ImageSize;
    rightImage?: ReactNode;
    rightImageSize?: ImageSize;
    variant?: 'light' | 'normal';
    /** The vertical spacing between title and subtitle. */
    textSpacing?: number;
    titleColor?: string;
    subtitleColor?: string;
    subtitleVariant?: TypographyProps['variant'];
    /** The padding applied around the image. */
    imagePadding?: number;
    textWrapperStyle?: SxProps<Theme>;
    titleStyle?: SxProps<Theme>;
    sx?: SxProps<Theme>;
    caption?: React.JSX.Element;
    captionWrapperStyle?: SxProps<Theme>;
    secondaryAction?: BannerAction | CustomBannerAction;
    isLoading?: boolean;
    imageLoadingStyle?: SxProps<Theme>;
}

export type BannerPropsWithPrimaryAction = {
    primaryAction: BannerAction;
} & (
    | { isEntirelyClickable: false; icon?: never }
    | { isEntirelyClickable?: never; icon?: never }
    | { isEntirelyClickable: true; icon?: ReactElement }
);
export interface BannerPropsWithoutPrimaryAction {
    primaryAction?: undefined;
    isEntirelyClickable?: never;
    icon?: never;
}
export type BannerProps = BannerBaseProps &
    (BannerPropsWithPrimaryAction | BannerPropsWithoutPrimaryAction);

type ContainerProps = Partial<
    Pick<BannerAction, 'onClickAction' | 'trackAction'>
> & {
    isEntirelyClickable: boolean;
    actionProps?: ActionProps;
    clickableLabel?: string;
    isLoading?: boolean;
};

const Container = ({
    children,
    onClickAction,
    actionProps,
    trackAction,
    clickableLabel,
    isEntirelyClickable,
    isLoading,
}: PropsWithChildren<ContainerProps>) => {
    if (
        (onClickAction || actionProps?.href) &&
        trackAction &&
        isEntirelyClickable &&
        !isLoading
    ) {
        return (
            <>
                <ButtonBase
                    {...actionProps}
                    sx={styleOverlaidButton}
                    trackButton={trackAction}
                    onClick={onClickAction}
                    aria-label={clickableLabel}
                />
                <Box sx={styleChildrenWrapper}>{children}</Box>
            </>
        );
    }

    return <>{children}</>;
};

function Action({
    isEntirelyClickable,
    icon,
    secondaryAction,
    primaryAction,
    isLoading,
}: Pick<
    BannerProps,
    | 'isEntirelyClickable'
    | 'icon'
    | 'secondaryAction'
    | 'primaryAction'
    | 'isLoading'
>) {
    if (isLoading) {
        return null;
    }

    if (isEntirelyClickable && icon) {
        return (
            <Grid item ml="auto">
                {icon}
            </Grid>
        );
    }

    const isPrimaryActionVisible = primaryAction && !isEntirelyClickable;

    return (
        <>
            {isPrimaryActionVisible && (
                <Grid item ml="auto">
                    <Button
                        variant="outlined"
                        sx={styleButton}
                        {...primaryAction.actionProps}
                        trackButton={primaryAction.trackAction}
                        onClick={primaryAction.onClickAction}
                    >
                        {primaryAction.actionText}
                    </Button>
                </Grid>
            )}
            {secondaryAction && (
                <Grid item ml={!isPrimaryActionVisible ? 'auto' : undefined}>
                    <StopPropagation>
                        {secondaryAction.isCustom ? (
                            secondaryAction.customAction
                        ) : (
                            <Button
                                variant="outlined"
                                sx={styleButton}
                                {...secondaryAction.actionProps}
                                trackButton={secondaryAction.trackAction}
                                onClick={secondaryAction.onClickAction}
                            >
                                {secondaryAction.actionText}
                            </Button>
                        )}
                    </StopPropagation>
                </Grid>
            )}
        </>
    );
}

const styleTextGrow = { flexGrow: 1 };

const styleImageSize = (size?: ImageSize) => {
    switch (size) {
        case 'medium':
            return styleImageMedium;
        case 'large':
            return styleImageLarge;
        default:
            return;
    }
};

export function Banner({
    title,
    subtitle,
    category,
    caption,
    primaryAction,
    secondaryAction,
    textSpacing = 1,
    titleColor = 'text.body',
    subtitleColor = 'text.sub',
    subtitleVariant,
    captionWrapperStyle,
    imagePadding = 4,
    textWrapperStyle,
    titleStyle,
    leftImage,
    leftImageSize,
    rightImage,
    rightImageSize,
    variant = 'normal',
    sx,
    isEntirelyClickable: _isEntirelyClickable,
    icon,
    isLoading = false,
    imageLoadingStyle: _imageLoadingStyle,
}: BannerProps) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
    const isEntirelyClickable = useMemo(
        () =>
            typeof _isEntirelyClickable === 'boolean'
                ? _isEntirelyClickable
                : isMobile,
        [_isEntirelyClickable, isMobile]
    );

    const contentStyle = useMemo(
        () => styleContent(imagePadding),
        [imagePadding]
    );

    const subtitleStyle = useMemo(
        () => styleSubtitle(textSpacing),
        [textSpacing]
    );

    const imageLoadingStyle = useMemo(
        () => styleImageLoading(imagePadding),
        [imagePadding]
    );

    const clickableBannerLabel = useMemo(
        () =>
            primaryAction?.actionProps?.['aria-label'] ??
            `${title ?? ''} ${subtitle ?? ''} ${
                primaryAction?.actionText ?? ''
            }`,
        [primaryAction?.actionProps, primaryAction?.actionText, subtitle, title]
    );

    return (
        <Paper sx={sxAppend(stylePaper, sx)}>
            <Container
                actionProps={primaryAction?.actionProps}
                onClickAction={primaryAction?.onClickAction}
                trackAction={primaryAction?.trackAction}
                isEntirelyClickable={isEntirelyClickable}
                clickableLabel={clickableBannerLabel}
                isLoading={isLoading}
            >
                <Grid
                    container
                    alignItems="center"
                    wrap="nowrap"
                    sx={contentStyle}
                    gap={imagePadding}
                >
                    <Grid item>
                        <Box
                            sx={sxAppend(
                                styleImage,
                                styleImageSize(leftImageSize)
                            )}
                        >
                            {isLoading ? (
                                <Box sx={styleImageLoadingWrapper}>
                                    <Skeleton
                                        sx={sxAppend(
                                            imageLoadingStyle,
                                            _imageLoadingStyle
                                        )}
                                        variant="rectangular"
                                    />
                                </Box>
                            ) : (
                                leftImage
                            )}
                        </Box>
                    </Grid>
                    <Grid item sx={sxAppend(textWrapperStyle, styleTextGrow)}>
                        {category && (
                            <Typography
                                variant={
                                    subtitleVariant ??
                                    (variant === 'light'
                                        ? 'subtitle1'
                                        : 'subtitle2')
                                }
                                component="p"
                                color={subtitleColor}
                                fontSize={12}
                                fontWeight={700}
                                mb={1}
                            >
                                {category}
                            </Typography>
                        )}
                        {isLoading ? (
                            <Skeleton width={getRandomIntBetween(100, 200)} />
                        ) : (
                            <Typography
                                variant={
                                    variant === 'light' ? 'h6' : 'body1Demi'
                                }
                                component="h2"
                                color={titleColor}
                                sx={titleStyle}
                                mb={1}
                            >
                                {title}
                            </Typography>
                        )}
                        {subtitle &&
                            (isLoading ? (
                                <Skeleton
                                    width={getRandomIntBetween(100, 200)}
                                />
                            ) : (
                                <Typography
                                    variant={
                                        subtitleVariant ??
                                        (variant === 'light'
                                            ? 'subtitle1'
                                            : 'subtitle2')
                                    }
                                    component="p"
                                    color={subtitleColor}
                                    sx={subtitleStyle}
                                >
                                    {subtitle}
                                </Typography>
                            ))}
                        {caption && (
                            <Box
                                sx={sxAppend(
                                    styleCaptionWrapper,
                                    captionWrapperStyle
                                )}
                            >
                                {caption}
                            </Box>
                        )}
                    </Grid>
                    {rightImage && (
                        <Grid item>
                            <Box
                                sx={sxAppend(
                                    styleImage,
                                    styleImageSize(rightImageSize)
                                )}
                            >
                                {isLoading ? (
                                    <Box sx={styleImageLoadingWrapper}>
                                        <Skeleton
                                            sx={sxAppend(
                                                imageLoadingStyle,
                                                _imageLoadingStyle
                                            )}
                                            variant="rectangular"
                                        />
                                    </Box>
                                ) : (
                                    rightImage
                                )}
                            </Box>
                        </Grid>
                    )}
                    <Action
                        isEntirelyClickable={isEntirelyClickable}
                        icon={icon}
                        primaryAction={primaryAction}
                        secondaryAction={secondaryAction}
                        isLoading={isLoading}
                    />
                </Grid>
            </Container>
        </Paper>
    );
}
